/**
 * Desempenho do atendimento (spec 096, US4).
 *
 * **POR QUE ESTA TELA É DO MÓDULO E NÃO DA PLATAFORMA.** Turno, conversa,
 * transbordo e funil não existem fora do atendimento. Ela era "Métricas" no
 * desenho antigo, no menu da plataforma, e mudou de lugar pelo mesmo motivo que
 * o blueprint virou passo do agente: o que descreve o atendimento mora com ele.
 *
 * **O FUNIL DECLARA O VAZIO COM O MOTIVO.** Escritório que não declarou objetivo
 * não tem o que medir, e a seção diz isso — não desenha um gráfico em branco
 * (regra de ouro 4: degradação termina em mensagem determinística).
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Gauge, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EscopoDaOrganizacao } from '@/components/layout/escopo-da-organizacao'
import {
  CartaoDeNumero,
  FaixaDePeriodo,
  SerieEmBarras,
} from '@/components/painel'
import { dolar, inteiro, numero } from '@/lib/formato'
import { useOrganizationStore } from '@/stores/organization-store'
import { desempenhoService, type Janela } from '@/services/painel'
import { useAtendimento } from '@/hooks/use-atendimento'
import type {
  Cascata,
  Conversa,
  Conversao,
  Fatia,
  Motivo,
  Pergunta,
  Ponto,
} from '@/schemas/painel'
import { cn } from '@/lib/utils'

const JANELAS = [
  { valor: 7, rotulo: '7 dias' },
  { valor: 14, rotulo: '14 dias' },
  { valor: 30, rotulo: '30 dias' },
] as const

function mediana(valores: number[]): number {
  if (valores.length === 0) return 0
  const ordenados = [...valores].sort((a, b) => a - b)
  const meio = Math.floor(ordenados.length / 2)
  return ordenados.length % 2 === 0
    ? (ordenados[meio - 1] + ordenados[meio]) / 2
    : ordenados[meio]
}

/**
 * O vazio que é FALHA DE LEITURA, e não observação.
 *
 * Sem ele, uma seção que não carregou cai no texto otimista — "toda busca
 * encontrou o que foi pedido" — e afirma um acerto que ninguém observou. A
 * regra de ouro 4 pede mensagem determinística; mensagem determinística
 * ERRADA é pior que erro, porque ninguém vai investigar.
 */
function NaoFoiPossivelLer() {
  return (
    <div className="rounded-lg border border-dashed border-amber-700/50 p-6 text-center">
      <p className="text-gray-300">Não foi possível ler esta seção agora</p>
      <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
        O número não está em branco porque é zero: ele não foi lido. Recarregue
        em instantes — se persistir, a falha está registrada no monitoramento.
      </p>
    </div>
  )
}

export function AtendimentoDesempenhoPage() {
  const { currentOrganization } = useOrganizationStore()
  const [dias, setDias] = useState<number>(14)
  // SPEC 157 (FR-013): o painel não supõe um agente por organização. Com dois ou
  // mais atendimentos aparece o seletor; "Todos" é a soma.
  const { agentes } = useAtendimento(currentOrganization?.id)
  const [agentId, setAgentId] = useState<string | null>(null)
  const [turnos, setTurnos] = useState<Ponto[]>([])
  const [conversas, setConversas] = useState<Conversa[]>([])
  const [transbordos, setTransbordos] = useState<Fatia[]>([])
  const [funil, setFunil] = useState<Conversao[]>([])
  // As três seções da spec 117. `aplicavel` diz QUAL dos dois vazios mostrar.
  // TRÊS ESTADOS POR SEÇÃO, e não dois. `null` é "não li", que é diferente de
  // "li e não havia nada". Com dois estados, a seção que FALHA cai no vazio
  // otimista e afirma um acerto que ninguém observou — que é exatamente o
  // defeito que o mecanismo `aplicavel` existe para impedir, cometido pelo
  // caminho do erro.
  const [cascata, setCascata] = useState<Cascata | null>(null)
  const [motivos, setMotivos] = useState<Motivo[] | null>(null)
  const [perguntas, setPerguntas] = useState<Pergunta[] | null>(null)
  const [aplicavel, setAplicavel] = useState<boolean | null>(null)
  const [falhou, setFalhou] = useState<Set<string>>(new Set())
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const buscar = useCallback(async () => {
    if (!currentOrganization) return
    setCarregando(true)
    const janela: Janela = { dias, agentId }
    try {
      // UMA SEÇÃO QUE FALHA NÃO DERRUBA AS OUTRAS. `allSettled`, e não `all`:
      // com `all`, um erro em "perguntas" apagaria a tela inteira, e quem
      // olhasse concluiria que o atendimento parou. Mesma regra do `try/except`
      // em volta do avanço do funil, e pelo mesmo motivo.
      const r = await Promise.allSettled([
        desempenhoService.turnosPorDia(currentOrganization.id, janela),
        desempenhoService.conversas(currentOrganization.id, janela),
        desempenhoService.transbordos(currentOrganization.id, janela),
        desempenhoService.funil(currentOrganization.id, janela),
        desempenhoService.cascata(currentOrganization.id, janela),
        desempenhoService.motivos(currentOrganization.id, janela),
        desempenhoService.perguntas(currentOrganization.id, janela),
        desempenhoService.aplicabilidade(currentOrganization.id),
      ])
      if (r[0].status === 'fulfilled') setTurnos(r[0].value)
      if (r[1].status === 'fulfilled') setConversas(r[1].value)
      if (r[2].status === 'fulfilled') setTransbordos(r[2].value)
      if (r[3].status === 'fulfilled') setFunil(r[3].value)
      // Seção que falhou volta a NÃO LIDA, e entra no conjunto de falhas.
      // Manter o valor anterior seria pior que o vazio otimista: ao trocar a
      // janela, a tela mostraria os números da janela passada sem dizer.
      const quebrou = new Set<string>()
      setCascata(r[4].status === 'fulfilled' ? r[4].value : (quebrou.add('cascata'), null))
      setMotivos(r[5].status === 'fulfilled' ? r[5].value : (quebrou.add('motivos'), null))
      setPerguntas(
        r[6].status === 'fulfilled' ? r[6].value : (quebrou.add('perguntas'), null)
      )
      setAplicavel(
        r[7].status === 'fulfilled'
          ? r[7].value.aplicavel
          : (quebrou.add('perguntas'), null)
      )
      setFalhou(quebrou)
      // AVISA SE QUALQUER UMA FALHOU, e mantém as que responderam.
      //
      // O primeiro desenho só avisava quando TODAS falhavam — e aí uma seção
      // quebrada ficaria muda, que é a degradação silenciosa que a regra de
      // ouro 4 proíbe. O teste que existia desde a spec 106 pegou isso.
      setErro(
        r.some((x) => x.status === 'rejected')
          ? 'Não foi possível ler o desempenho agora.'
          : null
      )
    } catch {
      setErro('Não foi possível ler o desempenho agora.')
    } finally {
      setCarregando(false)
    }
  }, [currentOrganization, dias, agentId])

  useEffect(() => {
    void buscar()
  }, [buscar])

  if (!currentOrganization) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Gauge className="mb-4 h-10 w-10 text-amber-400" />
        <p className="text-gray-400">
          Escolha uma organização para ver o desempenho.
        </p>
      </div>
    )
  }

  const totalDeTurnos = turnos.reduce((soma, p) => soma + (p.valor ?? 0), 0)
  const custos = conversas.map((c) => c.cost)
  const custoTotal = custos.reduce((a, b) => a + b, 0)
  const transbordados = transbordos.reduce((soma, f) => soma + f.quantidade, 0)
  const porConversa = conversas.length > 0 ? custoTotal / conversas.length : 0
  const fatiaTransbordada =
    conversas.length > 0 ? (transbordados / conversas.length) * 100 : 0

  return (
    <div className="space-y-6">
      {/* O cabeçalho já nomeia a tela; aqui fica só o que dá contexto. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <FaixaDePeriodo
            opcoes={JANELAS}
            valor={dias}
            aoEscolher={setDias}
            rotulo="Período"
          />
          {agentes.length > 1 && (
            // SPEC 157 (FR-013): com um atendimento só o seletor não aparece.
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <span>Atendimento</span>
              <select
                aria-label="Atendimento"
                className="h-9 rounded-md border border-gray-700/60 bg-white/5 px-2 text-sm text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60"
                value={agentId ?? ''}
                onChange={(e) => setAgentId(e.target.value || null)}
              >
                <option value="">Todos os atendimentos</option>
                {agentes.map((a) => (
                  <option key={a.agent_id} value={a.agent_id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p className="text-sm text-gray-500">
            Turno, conversa e transbordo — o que só existe com o módulo.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EscopoDaOrganizacao nome={currentOrganization.name} />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Atualizar"
            onClick={() => void buscar()}
          >
            <RefreshCw className={cn('h-4 w-4', carregando && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {erro && <p className="text-sm text-red-400">{erro}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <CartaoDeNumero
          titulo="Turnos no período"
          valor={inteiro(totalDeTurnos)}
          detalhe={`${numero(totalDeTurnos / dias)} por dia`}
        />
        <CartaoDeNumero
          titulo="Conversas"
          valor={inteiro(conversas.length)}
          detalhe={
            conversas.length > 0
              ? `${numero(
                  conversas.reduce((s, c) => s + c.turnos, 0) / conversas.length
                )} turnos por conversa`
              : 'nenhuma no período'
          }
        />
        <CartaoDeNumero
          titulo="Custo por conversa"
          valor={dolar(porConversa)}
          detalhe={`mediana ${dolar(mediana(custos))}`}
        />
        <CartaoDeNumero
          titulo="Transbordos"
          valor={inteiro(transbordados)}
          detalhe={
            conversas.length > 0
              ? `${numero(fatiaTransbordada, 0)}% das conversas`
              : 'sem conversa para comparar'
          }
        />
      </div>

      <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
        <p className="mb-3 text-sm font-medium text-gray-300">
          Turnos por dia, nos últimos {dias} dias
        </p>
        <SerieEmBarras
          pontos={turnos}
          formatar={(v) => `${v} turnos`}
          legenda="turnos atendidos"
        />
      </div>

      {transbordos.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-800/50">
          <p className="bg-white/5 px-4 py-2 text-sm font-medium text-gray-300">
            Transbordos por motivo
          </p>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-800/50">
              {transbordos.map((fatia) => (
                <tr key={fatia.categoria} className="text-gray-300">
                  <td className="px-4 py-2">{fatia.categoria}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {inteiro(fatia.quantidade)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
        <p className="mb-3 text-sm font-medium text-gray-300">Funil de conversão</p>
        {funil.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-700 p-6 text-center">
            <p className="text-gray-300">
              {currentOrganization.name} não declarou objetivos
            </p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
              O funil mede o caminho até um objetivo que a organização declara — uma
              visita agendada, uma proposta aceita. Sem objetivo declarado não há o
              que medir, e esta seção fica inerte de propósito: ela não consulta
              nada e não registra nada.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-2">Objetivo</th>
                <th className="py-2 text-right">Abertos</th>
                <th className="py-2 text-right">Convertidos</th>
                <th className="py-2 text-right">Taxa</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {funil.map((linha) => (
                <tr key={linha.objetivo} className="text-gray-300">
                  <td className="py-2">{linha.objetivo}</td>
                  <td className="py-2 text-right tabular-nums">
                    {inteiro(linha.abertos)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {inteiro(linha.convertidos)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {linha.taxa === null
                      ? '—'
                      : `${numero(linha.taxa * 100, 0)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* ONDE O FUNIL VAZA (spec 117)                                        */}
      {/*                                                                     */}
      {/* A cascata lê a etapa máxima que cada objetivo alcançou. O número    */}
      {/* que vale é a QUEDA entre etapas, não o total: a primeira etapa      */}
      {/* acende em toda conversa com ao menos um turno, porque o sinal dela  */}
      {/* é `turno_concluido` e não existe sinal de "pediu".                  */}
      {/* ------------------------------------------------------------------ */}
      <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
        <p className="mb-3 text-sm font-medium text-gray-300">Onde o funil vaza</p>
        {falhou.has('cascata') ? (
          <NaoFoiPossivelLer />
        ) : !cascata || cascata.etapas.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-700 p-6 text-center">
            <p className="text-gray-300">Nenhum objetivo abriu ainda</p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
              O funil começa a contar na próxima conversa. Objetivos nascem daqui
              para frente — conversas anteriores à declaração não são
              reconstruídas, porque o sinal que as abriria não existia quando
              elas aconteceram.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {cascata.etapas.map((etapa) => {
              const base = cascata.etapas[0]?.alcancaram || 1
              const largura = Math.max(
                4,
                Math.round((etapa.alcancaram / base) * 100)
              )
              return (
                <div key={etapa.indice} className="flex items-center gap-4">
                  {/* O NOME VEM DO DOCUMENTO DO ESCRITÓRIO (FR-009). O
                      índice só aparece quando o dado tem etapa que a versão
                      publicada não declara mais — e aí a ausência é
                      informação, não enfeite. */}
                  <div className="w-36 truncate text-sm text-gray-400">
                    {etapa.nome ?? `etapa ${etapa.indice}`}
                  </div>
                  <div className="h-8 flex-grow overflow-hidden rounded bg-white/5">
                    <div
                      className="flex h-full items-center bg-gradient-to-r from-indigo-600 to-violet-600 pl-3"
                      style={{ width: `${largura}%` }}
                    >
                      <span className="text-sm font-semibold tabular-nums text-white">
                        {inteiro(etapa.alcancaram)}
                      </span>
                    </div>
                  </div>
                  {/* A QUEDA LEVA À LISTA JÁ FILTRADA, e não a uma tela
                      própria: cada linha de lá já é o que se quer abrir. O
                      recorte é quem parou nesta etapa — os que chegaram aqui e
                      não passaram. */}
                  <div className="w-32 text-right text-sm tabular-nums">
                    {etapa.queda_ate_aqui > 0 ? (
                      <Link
                        to={
                          `/executions?etapa=${etapa.indice - 1}` +
                          `&contando=conversas&dias=${dias}` +
                          (cascata.objetivo ? `&objetivo=${cascata.objetivo}` : '')
                        }
                        className="text-amber-400 underline-offset-2 hover:underline"
                      >
                        −{inteiro(etapa.queda_ate_aqui)} aqui
                      </Link>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              )
            })}
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              A primeira etapa acende em{' '}
              <strong className="font-semibold text-gray-400">
                toda conversa com ao menos um turno
              </strong>
              , e não só nas que pedem algo — não existe sinal de pedido, e
              criá-lo custaria uma chamada de modelo por turno. Por isso a taxa
              sobre este denominador subestima o desempenho, e{' '}
              <strong className="font-semibold text-gray-400">
                o número útil é a queda entre etapas
              </strong>
              , não o total.
            </p>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* POR QUE NÃO CONVERTEU — com a FONTE de cada motivo visível.         */}
      {/* Sem a fonte, palpite vira medição.                                  */}
      {/* ------------------------------------------------------------------ */}
      <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
        <p className="mb-3 text-sm font-medium text-gray-300">Por que não converteu</p>
        {falhou.has('motivos') ? (
          <NaoFoiPossivelLer />
        ) : (motivos ?? []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-700 p-6 text-center">
            <p className="text-gray-300">Nenhuma conversa deixou de converter</p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
              Ou todas chegaram ao fim, ou as que abriram ainda estão dentro da
              janela declarada. Esta seção não afirma sucesso: ela diz que ainda
              não há desfecho negativo para explicar.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-2">Motivo</th>
                <th className="py-2">Como sabemos</th>
                <th className="py-2 text-right">Conversas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {(motivos ?? []).map((linha) => (
                <tr key={`${linha.motivo}-${linha.fonte}`} className="text-gray-300">
                  <td className="py-2">{linha.motivo}</td>
                  <td className="py-2">
                    <span
                      className={
                        linha.fonte === 'observado'
                          ? 'rounded bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400'
                          : linha.fonte === 'inferido'
                            ? 'rounded bg-violet-500/10 px-2 py-0.5 text-xs text-violet-400'
                            : 'rounded bg-gray-500/10 px-2 py-0.5 text-xs text-gray-400'
                      }
                    >
                      {linha.fonte}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {inteiro(linha.quantidade)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* O QUE PERGUNTARAM E ELE NÃO SOUBE — e os DOIS vazios.               */}
      {/*                                                                     */}
      {/* `aplicavel` vem do cadastro de ferramentas do escritório. Sem busca  */}
      {/* no acervo, dizer "nenhuma ficou sem resposta" afirmaria um acerto    */}
      {/* que ninguém observou.                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
        <p className="mb-3 text-sm font-medium text-gray-300">
          O que perguntaram e ele não soube responder
        </p>
        {falhou.has('perguntas') ? (
          <NaoFoiPossivelLer />
        ) : aplicavel === false ? (
          <div className="rounded-lg border border-dashed border-violet-700/50 p-6 text-center">
            <p className="text-gray-300">
              Esta seção não se aplica a {currentOrganization.name}
            </p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-gray-400">
              Ela conta buscas no acervo que não acharam, e esta organização não
              tem acervo habilitado. Não é que nada ficou sem resposta: é que não
              há o que medir aqui.
            </p>
          </div>
        ) : (perguntas ?? []).length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-700 p-6 text-center">
            <p className="text-gray-300">Nenhuma pergunta ficou sem resposta</p>
            <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
              Toda busca no acervo encontrou o que foi pedido. Se isso durar,
              vale conferir contra o total de buscas — lista vazia por acerto e
              lista vazia por gatilho quebrado têm a mesma aparência, e foi assim
              que esta tabela passou dois meses em zero.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-2 text-right">Vezes</th>
                <th className="py-2 pl-4">Pergunta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {/* A PERGUNTA LEVA ÀS CONVERSAS QUE A FIZERAM, como a queda
                  leva às que pararam. Saber que perguntaram duas vezes sem
                  poder ver quais é o número sem o caso — e é no caso que está
                  o que fazer a respeito.

                  O recorte vai pela CHAVE, não pelo texto: o texto é `min()`
                  do grupo, e duas normalizações do mesmo hash dariam listas
                  diferentes.

                  Pergunta antiga, de antes de a chave existir, não vira link:
                  sem endereço não há para onde ir, e um link que não recorta
                  seria pior que texto. */}
              {(perguntas ?? []).map((linha) => (
                <tr key={linha.chave || linha.pergunta} className="text-gray-300">
                  <td className="w-16 py-2 text-right font-semibold tabular-nums text-amber-400">
                    {inteiro(linha.quantas)}
                  </td>
                  <td className="py-2 pl-4">
                    {linha.chave ? (
                      <Link
                        to={
                          `/executions?pergunta=${encodeURIComponent(linha.chave)}` +
                          `&contando=conversas&dias=${dias}`
                        }
                        className="underline-offset-2 hover:underline"
                      >
                        {linha.pergunta}
                      </Link>
                    ) : (
                      linha.pergunta
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
